import { BehaviorSubject, combineLatest, of } from 'rxjs'
import { reduce, filter, skip } from 'rxjs/operators'
import Route from 'route-parser'
import debug from './debug'
import { find, includes, isArray } from '../libraries/lodash'
import { observe, updateHistoryState, ROOT_URL, replaceUrl } from './history'
import { register, toQueryString } from './url'
import { FLAVOURS, DEFAULT_FLAVOUR } from '../constants/flavours'
import render from './render'
import { parse } from 'qs'
import { flushPageState } from '../services/PageStateService'
import { isValidResponse, mapResponse } from '../helpers/dataflow'

export default class Router extends BehaviorSubject {
  constructor ({ routes, layout, $extend = of({}), urlMapping = (url) => url, sendPageViews = true }) {
    super(ROOT_URL)
    this.routes = routes.map(Router.readRoute)
    this.layout = layout
    this.previousPageId = null
    this.render = render(layout)
    this.urlMapping = urlMapping

    const $extended = isArray($extend)
      ? (
          $extend.pipe(reduce(($result, $stream) => {
            return combineLatest([$result, $stream], mapResponse((evt, ext) => ({ ...evt, ...ext })))
          }, observe()))
        )
      : combineLatest([observe(), $extend], mapResponse((evt, ext) => ({ ...evt, ...ext })))

    $extended.pipe(
      filter(isValidResponse)
    ).subscribe(this)

    this.start()
  }

  start = () => {
    this.pipe(filter(pathData => pathData !== '/')).subscribe(pathData => this.handle({ ...pathData }))
  }

  route = (url) => {
    const { controller, name, parser, title } = find(
      this.routes,
      Router.testRoute.bind(null, url)
    )
    const params = parser.match(url)
    return { controller, params, name, title }
  }

  handle = (evt) => {
    const { url, queryString } = evt
    const mappedUrl = this.urlMapping(url)
    const matchedRoute = this.route(mappedUrl)
    const query = parse(queryString || '')

    if (mappedUrl !== url) {
      replaceUrl(mappedUrl)
    }

    if (typeof matchedRoute.controller === 'function') {
      let pageId

      try {
        pageId = matchedRoute.controller(
          this.pipe(skip(1)),
          Object.assign(evt, {
            params: matchedRoute.params,
            query,
            previousPageId: this.previousPageId
          }),
          this.render
        )
        flushPageState(pageId)
        updateHistoryState({ viewName: matchedRoute.name, pageId })
        this.previousPageId = pageId
      } catch (e) {
        debug.error(e)
      }
    }
  }

  static readFlavour (flavour) {
    return includes(FLAVOURS, flavour) ? flavour : DEFAULT_FLAVOUR
  }

  static readRoute (routeArr) {
    routeArr = routeArr instanceof Controller ? routeArr.config : routeArr

    const [name, route, controller, title] = routeArr
    const parser = new Route(route)

    register(name, parser)
    return { name, parser, controller, title }
  }

  static testRoute (url, route) {
    return !!route.parser.match(url)
  }
}

export class Controller {
  constructor ({ name = null, route = null, controller = null, title = null }) {
    Controller.validate(name)
    Controller.validate(route)
    Controller.validate(controller)
    Object.assign(this, { name, route, controller, parser: new Route(route), title })
  }

  static validate (v, e) {
    if (v === null) {
      throw new Error('Incorrect controller definition')
    }
  }

  get config () {
    return [this.name, this.route, this.controller, this.title]
  }

  generateLink (params = {}, query = {}) {
    return this.parser.reverse(params) + toQueryString(query)
  }
}
